import AlternateEmailIcon from "@mui/icons-material/AlternateEmail";
import FacebookIcon from "@mui/icons-material/Facebook";
import InstagramIcon from "@mui/icons-material/Instagram";
import LanguageIcon from "@mui/icons-material/Language";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import TwitterIcon from "@mui/icons-material/Twitter";
import { Box, Button, Typography, Avatar, Tooltip } from "@mui/material";
import React from "react";

import { SocialMediaIdentifier } from "0.Mine/3.Interfaces/social";

// Import platform-specific icons

export interface Platform {
  id: string;
  name: string;
  color: string;
  type: SocialMediaIdentifier;
  image: string;
  // Optional validation properties
  isValid?: boolean;
  errorMessage?: string | null;
  validationIcon?: React.ReactNode;
}

interface PlatformSelectorProps {
  platforms: Platform[];
  selectedPlatforms: string[];
  onToggle: (platformId: string) => void;
}

// Helper function to get the appropriate icon for each platform type
const getPlatformIcon = (type: SocialMediaIdentifier): React.ReactNode => {
  switch (type) {
    case SocialMediaIdentifier.FACEBOOK:
      return <FacebookIcon fontSize="medium" />;
    case SocialMediaIdentifier.INSTAGRAM:
      return <InstagramIcon fontSize="medium" />;
    default:
      return <LanguageIcon fontSize="small" />;
  }
};

export const PlatformSelector: React.FC<PlatformSelectorProps> = ({
  platforms,
  selectedPlatforms,
  onToggle,
}) => {
  return (
    <Box>
      <Typography variant="body2" sx={{ fontWeight: 500, color: "grey.700", mb: 1 }}>
        Scegli dove pubblicare o schedulare il post
      </Typography>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
        {platforms.map((platform) => {
          const selected = selectedPlatforms.includes(platform.id);
          const showValidation = selected && platform.validationIcon && platform.isValid === false;
          const platformIcon = getPlatformIcon(platform.type);

          // Wrap in tooltip only if there's an error message
          const buttonElement = (
            <Button
              key={`button-${platform.id}`}
              onClick={() => onToggle(platform.id)}
              variant={selected ? "contained" : "outlined"}
              startIcon={
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* Platform-specific icon */}
                  <Box
                    sx={{
                      mr: 1,
                      color: selected ? "inherit" : platform.color,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {platformIcon}
                  </Box>

                  {/* Avatar image */}
                  <Avatar
                    src={platform.image}
                    alt={platform.name}
                    sx={{
                      width: 20,
                      height: 20,
                      mr: -0.5, // Adjust spacing between icon and text
                    }}
                  />
                </Box>
              }
              sx={{
                textTransform: "none",
                borderRadius: "9999px",
                px: 2,
                py: 0.5,
                fontSize: "0.875rem",
                fontWeight: 500,
                display: "flex",
                alignItems: "center",
                gap: 1,
                position: "relative",
                ...(selected
                  ? {
                      backgroundColor: platform.color,
                      color: "common.white",
                      borderColor: "transparent",
                      "&:hover": {
                        backgroundColor: platform.color,
                        opacity: 0.9,
                      },
                    }
                  : {
                      backgroundColor: "common.white",
                      color: "grey.700",
                      borderColor: "grey.300",
                      "&:hover": {
                        backgroundColor: "grey.50",
                      },
                    }),
              }}
            >
              {platform.name}

              {/* Validation icon indicator */}
              {showValidation && (
                <Box
                  sx={{
                    position: "absolute",
                    top: -5,
                    right: -5,
                    backgroundColor: "background.paper",
                    borderRadius: "50%",
                    padding: "1px",
                    border: "1px solid currentColor",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "0.7rem",
                    minWidth: 18,
                    minHeight: 18,
                  }}
                >
                  {platform.validationIcon}
                </Box>
              )}
            </Button>
          );

          // Apply tooltip conditionally
          return (
            <Box key={platform.id}>
              {showValidation && platform.errorMessage ? (
                <Tooltip title={platform.errorMessage} arrow placement="top">
                  {buttonElement}
                </Tooltip>
              ) : (
                buttonElement
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};
